/**
 * External Imports
 */
import { FC, RefObject, useEffect, useRef, useState } from 'react';

/**
 * Nexus Imports
 */
import {
  NeuCheckbox,
  NeuIcon,
  NeuInput,
  NeuButton,
  NeuTextarea
} from '@neutron/react';

/**
 * Internal Imports
 */
import MultiSelectDropdown from '../../../multiSelect/MultiSelectDropdown';
import { Dropdown } from '../../../shared/dropdowns';
/**
 * Global Type Definition Imports
 */
import {
  ConditionListProp,
  QuestionPartProp
} from '../../../../config/interfaces';
/**
 * Style Imports
 */
import {
  InputHeading,
  NavIconLabel,
  QuestionPartCard,
  QuestionPartCardHeader,
  QuestionPartCardContents,
  QuestionPartBlueNavContainer,
  QuestionPartNavContainer,
  QuestionPartOptionInputContainer,
  QuestionPartType
  // StyledLabel
} from '../AddQuestionModal.styles';

interface IOptionState {
  flag: boolean;
  charge: boolean;
}

interface Props {
  editQuestion: boolean;
  part: QuestionPartProp;
  manuallyChangedOptions: RefObject<Record<string, boolean>>;
  conditionsList: ConditionListProp[];
  moveUpDown: (id: string, updown: string) => void;
  removeQuestionPart: (id: string) => void;
  handleQuestionPromptChange: (id: string, e: any) => void;
  addConditionToQuestionPart: (
    id: string,
    conditions: ConditionListProp[]
  ) => void;
  addToQuestionPartConfig: (id: string, key: string, value: string) => void;
  handleMustShowFlag: (
    id: string,
    e: React.BaseSyntheticEvent<MouseEvent>
  ) => void;
  updateMaxMin: (id: string, maxmin: string, e: any) => void;
  changeOptionTitle: (
    questionPartId: string,
    optionId: string,
    title: string
  ) => void;
  changeOptionValue: (
    questionPartId: string,
    optionId: string,
    value: string
  ) => void;
  addFlagToAnswer: (
    questionPartId: string,
    optionId: string,
    flag: string
  ) => void;
  handleChargeChange: (
    partId: string,
    optionId: string,
    charge: string
  ) => void;
  closeAnswer: (id: string) => void;
  addAnswer: (questionPartId: string) => void;
}

const chargeOptions = ['positive', 'negative'];

const QuestionPart: FC<Props> = ({
  editQuestion,
  part,
  manuallyChangedOptions,
  conditionsList,
  moveUpDown,
  removeQuestionPart,
  handleQuestionPromptChange,
  addConditionToQuestionPart,
  addToQuestionPartConfig,
  handleMustShowFlag,
  updateMaxMin,
  changeOptionTitle,
  changeOptionValue,
  addFlagToAnswer,
  handleChargeChange,
  closeAnswer,
  addAnswer
}) => {
  const manuallyChangedRef = manuallyChangedOptions;
  const [defaultSelection, setDefaultSelection] = useState<{}[]>([]);
  const [openStates, setOpenStates] = useState<IOptionState[]>([]);
  const focusedValueField = useRef<string | null>(null);

  useEffect(() => {
    if (editQuestion) {
      setDefaultSelection(
        part.questionCondition.map((val: string, i: number) => {
          return { value: val, id: i };
        })
      );
    }
  }, []);

  useEffect(() => {
    setOpenStates(
      part.options.map(() => ({
        flag: false,
        charge: false
      }))
    );
  }, [part.options.length]);

  return (
    <QuestionPartCard unit="card" className="w-auto h-auto my-1">
      <QuestionPartCardHeader unit="header" className="w-100">
        <QuestionPartType>{part.questionType}</QuestionPartType>
        <div className="d-flex">
          <QuestionPartNavContainer
            className="ms-3"
            onClick={() => {
              moveUpDown(part.questionPartId, 'up');
            }}
            aria-hidden="true"
          >
            <NeuIcon size="1x">arrow_upward</NeuIcon>
            <NavIconLabel>Move Up</NavIconLabel>
          </QuestionPartNavContainer>
          <QuestionPartNavContainer
            className="ms-1"
            onClick={() => {
              moveUpDown(part.questionPartId, 'down');
            }}
            aria-hidden="true"
          >
            <NeuIcon size="1x">arrow_downward</NeuIcon>
            <NavIconLabel>Move Down</NavIconLabel>
          </QuestionPartNavContainer>
          <QuestionPartBlueNavContainer
            className="ms-2"
            onClick={() => {
              removeQuestionPart(part.questionPartId);
            }}
            aria-hidden="true"
          >
            <NeuIcon size="1x">cancel</NeuIcon>
            <NavIconLabel>Remove</NavIconLabel>
          </QuestionPartBlueNavContainer>
        </div>
      </QuestionPartCardHeader>
      <QuestionPartCardContents unit="content">
        <NeuTextarea
          id="Prompt-Text-Area"
          label="Question prompt"
          required
          spellCheck
          value={part.question}
          onNeuChange={(e: any) => {
            handleQuestionPromptChange(part.questionPartId, e);
          }}
        />
        <InputHeading>Conditions</InputHeading>
        <div className="w-100">
          {/* TODO: This needs to go in lieu of the custom dropdown */}
          <MultiSelectDropdown
            options={conditionsList}
            defaultselection={defaultSelection}
            selected={(e: ConditionListProp[]) => {
              addConditionToQuestionPart(part.questionPartId, e);
            }}
            id="questionConditions"
            showArrow
            searchable={false}
            chipsInside={false}
            placeholderText="Select one or more..."
          />
        </div>
        <div className="d-flex ms-1" style={{ padding: '0.25rem 0 0.25rem' }}>
          <NeuCheckbox
            label="Must show?"
            onClick={e => handleMustShowFlag(part.questionPartId, e)}
          />
        </div>
        {part?.questionType?.toLowerCase() === 'scale' && (
          <div className="d-flex">
            <QuestionPartOptionInputContainer
              className="d-flex flex-column"
              style={{ flexBasis: '35%' }}
            >
              <NeuInput
                disabled
                label="Min"
                required
                type="number"
                style={{ gap: 0 }}
                onNeuChange={(e: any) => {
                  updateMaxMin(part.questionPartId, 'min', e);
                }}
                value={1} // hard-coding for now - scale is set to 1-10
              />
            </QuestionPartOptionInputContainer>
            <QuestionPartOptionInputContainer
              className="d-flex flex-column ms-1"
              style={{ flexBasis: '35%' }}
            >
              <NeuInput
                disabled
                label="Max"
                required
                type="number"
                style={{ gap: 0 }}
                onNeuChange={(e: any) => {
                  updateMaxMin(part.questionPartId, 'max', e);
                }}
                value={10} // hard-coding for now - scale is set to 1-10
              />
            </QuestionPartOptionInputContainer>
          </div>
        )}
        {part?.questionType?.toLowerCase() === 'aggregate' && (
          <div className="d-flex">
            <QuestionPartOptionInputContainer
              className="d-flex flex-column"
              style={{ flexBasis: '30%' }}
            >
              <NeuInput
                label="Maximum answers"
                required
                type="number"
                value={part?.config.maxAnswers}
                onNeuChange={e => {
                  addToQuestionPartConfig(
                    part.questionPartId,
                    'maxAnswers',
                    String(e.detail.value)
                  );
                }}
              />
            </QuestionPartOptionInputContainer>
            <QuestionPartOptionInputContainer
              className="d-flex flex-column ms-1"
              style={{ flexBasis: '35%' }}
            >
              <NeuInput
                label="Aggregation value"
                required
                value={part?.config.aggregationValue}
                onNeuChange={e => {
                  addToQuestionPartConfig(
                    part.questionPartId,
                    'aggregationValue',
                    String(e.detail.value)
                  );
                }}
              />
            </QuestionPartOptionInputContainer>
            <QuestionPartOptionInputContainer
              className="d-flex flex-column ms-1"
              style={{ flexBasis: '35%' }}
            >
              <NeuInput
                label="Value if none selected"
                required
                value={part?.config.defaultValue}
                onNeuChange={e => {
                  addToQuestionPartConfig(
                    part.questionPartId,
                    'defaultValue',
                    String(e.detail.value)
                  );
                }}
              />
            </QuestionPartOptionInputContainer>
          </div>
        )}
        {part.options.map((option: any, i: number) => {
          return (
            <div
              className="d-flex align-items-flex-start"
              key={option.optionId}
            >
              <QuestionPartOptionInputContainer className="d-flex flex-column">
                <InputHeading>Answer (i.e. Yes)</InputHeading>
                <NeuInput
                  label=""
                  required
                  spellCheck
                  value={option.title}
                  onNeuChange={e => {
                    changeOptionTitle(
                      part.questionPartId,
                      option.optionId,
                      String(e.detail.value)
                    );
                  }}
                />
              </QuestionPartOptionInputContainer>
              <QuestionPartOptionInputContainer className="d-flex flex-column ms-1">
                <InputHeading>Value (Optional)</InputHeading>
                <NeuInput
                  label=""
                  spellCheck
                  value={option.value}
                  onNeuFocus={() => {
                    manuallyChangedRef.current![option.optionId] = true;
                    focusedValueField.current = option.optionId;
                  }}
                  onNeuBlur={() => {
                    if (option.value === option.title) {
                      delete manuallyChangedRef.current![option.optionId];
                    }
                    focusedValueField.current = '';
                  }}
                  onNeuChange={e => {
                    if (focusedValueField.current !== option.optionId) {
                      return;
                    }
                    changeOptionValue(
                      part.questionPartId,
                      option.optionId,
                      String(e.detail.value)
                    );
                  }}
                />
              </QuestionPartOptionInputContainer>
              <QuestionPartOptionInputContainer className="d-flex flex-column ms-1">
                <InputHeading>Label</InputHeading>
                <Dropdown
                  classes="ps-0 pt-0"
                  name="flag"
                  open={openStates[i]?.flag}
                  placeholder={option.flag ? option.flag : ''}
                  styles={{ minWidth: '113px' }}
                  setOpen={() => {
                    setOpenStates(
                      openStates.map((s: IOptionState, j: number) => {
                        return i === j
                          ? { ...s, flag: !s.flag }
                          : { ...s, flag: false };
                      })
                    );
                  }}
                >
                  <div
                    key={1}
                    className={`dropdown-hover-item${
                      option.flag === '' ? ' selected' : ''
                    }`}
                    aria-selected={option.flag === ''}
                    role="option"
                    tabIndex={0}
                    onClick={() => {
                      addFlagToAnswer(part.questionPartId, option.optionId, '');
                    }}
                    onKeyDown={() => {
                      addFlagToAnswer(part.questionPartId, option.optionId, '');
                    }}
                  >
                    None
                  </div>
                  {conditionsList.map((condition: ConditionListProp) => (
                    <div
                      key={condition.id}
                      className={`dropdown-hover-item${
                        option.flag === condition.value ? ' selected' : ''
                      }`}
                      aria-selected={option.flag === condition.value}
                      role="option"
                      tabIndex={0}
                      onClick={() => {
                        addFlagToAnswer(
                          part.questionPartId,
                          option.optionId,
                          condition.value
                        );
                      }}
                      onKeyDown={() => {
                        addFlagToAnswer(
                          part.questionPartId,
                          option.optionId,
                          condition.value
                        );
                      }}
                    >
                      {condition.value}
                    </div>
                  ))}
                </Dropdown>
              </QuestionPartOptionInputContainer>
              <QuestionPartOptionInputContainer
                className="d-flex flex-column ms-1"
                style={{ flexBasis: '15%' }}
              >
                <InputHeading>Charge</InputHeading>
                <Dropdown
                  classes="ps-0 pt-0"
                  name="charge"
                  open={openStates[i]?.charge}
                  placeholder={option.charge}
                  styles={{ minWidth: '100px' }}
                  setOpen={() => {
                    setOpenStates(
                      openStates.map((s: IOptionState, j: number) => {
                        return i === j
                          ? { ...s, charge: !s.charge }
                          : { ...s, charge: false };
                      })
                    );
                  }}
                >
                  {chargeOptions.map((type: string) => (
                    <div
                      key={type}
                      className={`dropdown-hover-item${
                        option.charge === type ? ' selected' : ''
                      }`}
                      aria-selected={option.charge === type}
                      role="option"
                      tabIndex={0}
                      onClick={() => {
                        handleChargeChange(
                          part.questionPartId,
                          option.optionId,
                          type
                        );
                      }}
                      onKeyDown={() => {
                        handleChargeChange(
                          part.questionPartId,
                          option.optionId,
                          type
                        );
                      }}
                    >
                      {type}
                    </div>
                  ))}
                </Dropdown>
              </QuestionPartOptionInputContainer>
              <NeuIcon
                color="blue"
                size="1x"
                style={{ cursor: 'pointer' }}
                onClick={() => closeAnswer(option.optionId)}
              >
                close
              </NeuIcon>
            </div>
          );
        })}
        {(part.questionType === 'single' || part.questionType === 'multi') && (
          <NeuButton
            id="Add-Question-Question-Part-Button"
            className="my-1"
            color="blue"
            size="small"
            onClick={() => addAnswer(part.questionPartId)}
          >
            Add Answer
          </NeuButton>
        )}
      </QuestionPartCardContents>
    </QuestionPartCard>
  );
};

export default QuestionPart;
